/**
 * Application-wide constants for ACARS History application
 */

// Routes
export const ROUTES = {
  ROOT: '/',
  CALLBACK: '/acarshistory/callback',
  OVERVIEW: '/acarshistory/overview',
  ADMIN: '/acarshistory/admin',
  ADMIN_USERS: '/acarshistory/admin/users',
  ACCESS_DENIED: '/access-denied',
  BETA_OVERVIEW: '/acarshistory/beta/overview',
} as const;

// Airlines
export const AIRLINES = {
  AS: 'AS',
  QX: 'QX',
} as const;

export const AIRLINE_DISPLAY_NAMES: Record<string, string> = {
  [AIRLINES.AS]: 'Alaska',
  [AIRLINES.QX]: 'Horizon',
};

// LocalStorage Keys
export const STORAGE_KEYS = {
  DISCLAIMER_ACCEPTED: 'disclaimerAccepted',
  OIDC_USER_PREFIX: 'oidc.user:',
  OVERVIEW_VISITED: 'overviewVisited',
  BETA_THEME: 'betaTheme',
} as const;

// App Insights Event Names
export const APP_INSIGHTS_EVENTS = {
  OVERVIEW_PAGE_VISIT: 'OverviewPageVisit',
  DISCLAIMER_ACCEPTED: 'DisclaimerAccepted',
  SEARCH_EXECUTED: 'SearchExecuted',
  CSV_EXPORT: 'CSVExport',
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

// Date Range
export const DATE_RANGE = {
  MODES: {
    RELATIVE: 'relative',
    SPECIFIC: 'specific',
  },
  TIME_UNITS: {
    MINUTES: 'min',
    HOURS: 'hours',
    DAYS: 'days',
  },
  DEFAULT_RELATIVE_VALUE: 30,
  DEFAULT_RELATIVE_UNIT: 'min' as 'min',
} as const;

// Message Types
export const MESSAGE_TYPES = {
  XML: 1,
  NON_XML: 0,
} as const;

// Sort Directions
export const SORT_DIRECTIONS = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;

// Report Types
export const REPORT_TYPES = {
  ACCESSED: 'accessed',
  SEARCHED: 'searched',
} as const;

// ALPA Contact
export const ALPA_CONTACT = {
  NAME: 'James Falen',
  EMAIL: 'James.Falen@alaskaair.com',
} as const;

// Disclaimer Text
export const DISCLAIMER_TEXT = {
  HEADING: 'Restricted: ACARS History Data',
  NOTICE: `NOTICE: By logging into this system and accessing any ACARS Data, you are acknowledging that the Data is highly sensitive, and will not be shared (a) outside of Alaska Airlines, or (b) with any individual within Alaska Airlines who does not have access to ACARS Data or who does not otherwise have consent from ALPA. There are restrictions on the access and use of ACARS History data. It is prohibited to share ACARS data with third parties without written permission from ALPA. All messages accessed via ACARS History are considered identified data. ACARS data cannot be used to discipline or monitor pilot performance. You are responsible for the appropriate use and protection of any identified ACARS data that you access. Alaska Airlines policy is to purge identified ACARS data after three calendar months.`,
  PERMISSION_REQUEST: `To request ALPA permission for ACARS data, contact ${ALPA_CONTACT.NAME} at: ${ALPA_CONTACT.EMAIL}`,
} as const;

// Validation Patterns
export const VALIDATION = {
  FROM_TO_PATTERN: /^([A-Z]{3})\/([A-Z]{3})$/,
  FLIGHT_NUMBER_PATTERN: /\D/g,
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  LOAD_GROUPS_FAILED: 'Failed to load allowed groups',
  SAVE_GROUP_FAILED: 'Failed to save group',
  DELETE_GROUP_FAILED: 'Failed to delete group',
  UPDATE_STATUS_FAILED: 'Failed to update group status',
  LOAD_AIRLINES_FAILED: 'Error loading airlines',
  NO_USER_DATA: 'Error: Unable to load user data.',
  PARSE_USER_DATA_ERROR: 'Error parsing stored user data',
  NO_USER_DATA_WARNING: 'No user data found in local storage',
  INVALID_FROM_TO: 'Format must be XXX/XXX (e.g., SEA/LAX)',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  GROUP_CREATED: 'Group created successfully',
  GROUP_UPDATED: 'Group updated successfully',
  GROUP_DELETED: 'Group deleted successfully',
  DISCLAIMER_ACCEPTED: 'Disclaimer accepted. Reloading the page.',
  DISCLAIMER_CANCELED: 'Disclaimer canceled. Navigating back to acarshistory.',
} as const;

// Component Labels
export const LABELS = {
  APP_TITLE: 'ACARS HISTORY',
  ADMIN_TITLE: 'ACARS History - Admin Allowed Groups',
  OVERVIEW_TITLE: 'ACARS History',
  ACCESS_DENIED_TITLE: 'ACARS History - Access Denied',
  MANAGE_ALLOWED_GROUPS: 'Manage Allowed Groups',
  FILTER_BY_AIRLINE: 'Filter by Airline:',
  ALL_AIRLINES: 'All Airlines',
  AIRLINE_LABEL: 'Airline:',
  BACK_TO_OVERVIEW: '← Back to Overview',
  ADD_NEW_GROUP: '+ Add New Group',
  LOADING: 'Loading...',
  CHECKING_PERMISSIONS: 'Checking permissions...',
  NO_GROUPS_FOUND: 'No groups found. Click "Add New Group" to create one.',
} as const;

// Button Labels
export const BUTTON_LABELS = {
  ACCEPT: 'ACCEPT',
  CANCEL: 'CANCEL',
  EDIT: 'Edit',
  DELETE: 'Delete',
  CREATE: 'Create',
  UPDATE: 'Update',
  SEARCH: 'Search',
  RESET: 'Reset',
  EXPORT_CSV: 'Export CSV',
  PRINT: 'Print',
} as const;

// Table Column Headers
export const TABLE_HEADERS = {
  GROUP_NAME: 'Group Name',
  STATUS: 'Status',
  CREATED_DATE: 'Created Date',
  MODIFIED_DATE: 'Modified Date',
  ACTIONS: 'Actions',
} as const;

// Status Labels
export const STATUS_LABELS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
} as const;
